import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api'; // Use relative URL for same-origin API calls
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();

      // Re-validate the subscription whenever the user returns to the app —
      // catches the case where a SW update fired `pushsubscriptionchange` while
      // they were away, leaving the backend out of sync with the new browser keys.
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          setPermission(Notification.permission);
          checkSubscription();
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', onVisible);
      return () => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onVisible);
      };
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      
      // If permission is granted but no subscription exists, auto-resubscribe
      // This handles service worker updates after deployment that invalidate old subscriptions
      // BUT skip if the user intentionally disabled push notifications
      const userOptedOut = localStorage.getItem('push_manually_disabled') === 'true';
      if (!subscription && Notification.permission === 'granted' && VAPID_PUBLIC_KEY && !userOptedOut) {
        try {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
          });
          console.log('Auto-resubscribed to push after service worker update');
        } catch (resubErr) {
          console.error('Auto-resubscribe failed:', resubErr);
        }
      }
      
      setIsSubscribed(!!subscription);
      
      // If browser has a subscription, sync it to the backend
      if (subscription) {
        // Remember the user has had push enabled — used for recovery detection.
        try { localStorage.setItem('push_ever_enabled', '1'); } catch {}
        const token = localStorage.getItem('token');
        if (token) {
          // Send token to service worker for authenticated SW requests
          // Try controller first, fall back to registration.active
          const sw = navigator.serviceWorker.controller || registration.active;
          if (sw) {
            sw.postMessage({ type: 'SET_TOKEN', token });
          }
          const subJson = subscription.toJSON();
          try {
            await axios.post(`${API}/push/subscribe`, {
              endpoint: subJson.endpoint,
              keys: subJson.keys
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch {}
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    setLoading(true);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        console.log('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Clear opt-out flag since user is explicitly enabling
      localStorage.removeItem('push_manually_disabled');
      // Remember that this user has had push enabled at least once — used by the
      // PushRecoveryBanner to detect "permission was revoked by Chrome after silent failures" state.
      try { localStorage.setItem('push_ever_enabled', '1'); } catch {}

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        const vapidKey = VAPID_PUBLIC_KEY;
        if (!vapidKey) {
          console.error('VAPID public key not configured');
          setLoading(false);
          return false;
        }
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }

      // Sync subscription to backend
      const token = localStorage.getItem('token');
      if (token) {
        const sw = navigator.serviceWorker.controller || registration.active;
        if (sw) {
          sw.postMessage({ type: 'SET_TOKEN', token });
        }
        const subJson = subscription.toJSON();
        const syncPayload = { endpoint: subJson.endpoint, keys: subJson.keys };
        const syncHeaders = { headers: { Authorization: `Bearer ${token}` } };
        let syncOk = false;
        try {
          await axios.post(`${API}/push/subscribe`, syncPayload, syncHeaders);
          syncOk = true;
        } catch (err) {
          // Retry once after a short delay
          try {
            await new Promise(r => setTimeout(r, 1000));
            await axios.post(`${API}/push/subscribe`, syncPayload, syncHeaders);
            syncOk = true;
          } catch (retryErr) {
            console.error('Push subscription backend sync failed after retry:', retryErr);
          }
        }

        if (!syncOk) {
          setIsSubscribed(false);
          setLoading(false);
          return false;
        }
      }

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;

    setLoading(true);

    try {
      // Mark that user intentionally disabled push
      localStorage.setItem('push_manually_disabled', 'true');

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      // Capture endpoint before browser unsubscribe
      const endpointToRemove = subscription ? subscription.toJSON().endpoint : null;

      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from backend (device-specific if endpoint available)
      const token = localStorage.getItem('token');
      if (token) {
        if (endpointToRemove) {
          await axios.post(`${API}/push/unsubscribe-device`, {
            endpoint: endpointToRemove
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.delete(`${API}/push/unsubscribe`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
